import {
  ClosingCostsTotals,
} from '@elevationscu/common-mortgage-calculator';
import { ClosingCosts } from '../classes/closing-costs/ClosingCosts';
import { MonthlyPayments } from '../classes/closing-costs/MonthlyPayments';
import { EstimatedClosingCosts } from '../classes/closing-costs/EstimatedClosingCosts';
import { PrepaidExpenses } from '../classes/closing-costs/PrepaidExpenses';
import { FundsAtClosing } from '../classes/closing-costs/FundsAtClosing';
import { LoanDetails } from '../classes/closing-costs/LoanDetails';

export const helocClosingCostsMapping = (cashOutResults) => {
  const helocClosingCosts = new ClosingCosts();
  helocClosingCosts.fundsAtClosing.initialDrawFromLoan = cashOutResults?.cashReq;
  if (helocClosingCosts.fundsAtClosing.initialDrawFromLoan >= 250000) {
    helocClosingCosts.estimatedClosingCosts.lendersTitleInsurance = 450;
  }
  if (cashOutResults.addAppraisalFee) {
    helocClosingCosts.estimatedClosingCosts.appraisalFee = 650;
  } else {
    helocClosingCosts.estimatedClosingCosts.appraisalFee = 0;
  }
  const helocClosingCost = helocClosingCosts.estimatedClosingCosts.appraisalFee
    + helocClosingCosts.estimatedClosingCosts.creditReport
    + helocClosingCosts.estimatedClosingCosts.employmentVerification
    + helocClosingCosts.estimatedClosingCosts.floodDeterminationFee
    + helocClosingCosts.estimatedClosingCosts.lendersTitleInsurance
    + helocClosingCosts.estimatedClosingCosts.deedOfTrust
    + helocClosingCosts.estimatedClosingCosts.documentPreparationFee;

  const helocTotalFundsAtClosing = helocClosingCosts.fundsAtClosing.payoffMortgage
    + helocClosingCosts.fundsAtClosing.payoffOther
    + helocClosingCost
    - helocClosingCosts.fundsAtClosing.initialDrawFromLoan
    - helocClosingCosts.fundsAtClosing.addlLoan
    - helocClosingCosts.fundsAtClosing.appraisalFeeInAdvance;

  helocClosingCosts.fundsAtClosing.closingCosts = helocClosingCost;
  helocClosingCosts.fundsAtClosing.totalFundsAtClosing = helocTotalFundsAtClosing;

  helocClosingCosts.fundsAtClosing.calculatedLoanAmount = helocClosingCosts.fundsAtClosing.initialDrawFromLoan + helocClosingCosts.fundsAtClosing.addlLoan;
  return helocClosingCosts;
};

export const refiClosingCostsMapping = (cashOutResults, currentLoanAssumptions, ficoInfo) => {
  const calculatorMortgageDetailsInput = {
    cashAndMortage: Number(cashOutResults?.cashMort ?? 0),
    currentOriginalLoanAmount: Number(currentLoanAssumptions?.originalLoanVal ?? 0),
    currentInterestRate: Number(currentLoanAssumptions?.estimatedInterestRate ?? 0),
    taxesEscrow: Number(currentLoanAssumptions?.estimatedTaxes ?? 0),
    saleDate: ficoInfo?.saleDate ?? '1900-01-01T00:00:00',
    propertyInsuranteEscrow: Number(currentLoanAssumptions?.estimatedPropertyInsurance ?? 0),
    includeSecondLoan: currentLoanAssumptions?.includeSecondLoan,
  };

  const calculatorCostInput = {
    closingCosts: {
      underwritingFee: 500,
      processingFee: 450,
      discountPoints: 0,
      appraisalFee: 650,
      creditReport: 105,
      employmentVerification: 154,
      taxServiceFee: 70,
      floodDeterminationFee: 7,
      initialPMI: 0,
      settlement: 425,
      realEstateClosingFee: 0,
      lenderTitleInsurance: 0, // calculator should get this value
      taxCertificate: 25,
      misc: 0,
      cpl: 25,
      recording: 129, // deed of trust
      transferTax: 0,
      transferFee: 0,
    },
    prepaidExpenses: {
      interimInterestAmount: undefined, // calculator should get this value
      interimInterest: undefined, // calculator should get this value
      hoaDues: 0,
      pmi: 0,
      homeOwnerInsEscrow: undefined, // calculator should get this value
      homeOwnerInsPrePaid: undefined, // calculator should get this value
      floodInsuranceEscrow: 0,
      taxInsEscrow: undefined, // calculator should get this value
      monthsOfTaxes: undefined, // calculator should get this value
    },
    cashToClosing: {
      payoffFirstLoan: Number(currentLoanAssumptions?.estimatedMortageBalance ?? 0),
      payoffSecondLoan: currentLoanAssumptions?.includeSecondLoan === 'Yes' ? Number(currentLoanAssumptions?.estimatedSecondMortgageBalance ?? 0) : 0,
      baseLoanAmount: currentLoanAssumptions?.includeSecondLoan === 'Yes'
        ? (Number((currentLoanAssumptions?.estimatedMortageBalance ?? 0)) + Number((currentLoanAssumptions?.estimatedSecondMortgageBalance ?? 0)))
        : Number((currentLoanAssumptions?.estimatedMortageBalance ?? 0)),
      additionalLoanAmount: Number(cashOutResults?.cashReq ?? 0),
      closingCosts: undefined, // calculator should get this value
      prepaidExpenses: undefined, // calculator should get this value
      estimatedMonthlyPayment: cashOutResults.ltv >= 80 ? Number((currentLoanAssumptions?.estimatedMonthlyPayment ?? 0)) : Number((currentLoanAssumptions?.estimatedMonthlyPayment ?? 0) - (currentLoanAssumptions?.estimatedMortgageInsurance ?? 0)),
    },
  };
  calculatorCostInput.closingCosts.taxServiceFee = (calculatorCostInput.cashToClosing.baseLoanAmount + calculatorCostInput.cashToClosing.additionalLoanAmount) >= 1000000 ? 80 : 70;
  const closingCostTotals = new ClosingCostsTotals(calculatorCostInput, calculatorMortgageDetailsInput);
  const calculationResult = closingCostTotals.CalculateTotal();

  const refiClosingCosts = new ClosingCosts();
  refiClosingCosts.loanDetails = new LoanDetails();
  refiClosingCosts.loanDetails.interestRate = calculatorMortgageDetailsInput.currentInterestRate / 100;
  refiClosingCosts.loanDetails.loanAmount = calculatorMortgageDetailsInput.currentOriginalLoanAmount;

  refiClosingCosts.estimatedMonthlyPayments = new MonthlyPayments();
  refiClosingCosts.estimatedMonthlyPayments.principleInterest = currentLoanAssumptions?.estimatedPrincipalAndInterest;
  refiClosingCosts.estimatedMonthlyPayments.pmi = closingCostTotals.data.prepaidExpenses.pmi;
  refiClosingCosts.estimatedMonthlyPayments.homeownersInsuranceAmount = calculatorMortgageDetailsInput.propertyInsuranteEscrow;
  refiClosingCosts.estimatedMonthlyPayments.floodInsuranceAmount = closingCostTotals.data.prepaidExpenses.floodInsuranceEscrow;
  refiClosingCosts.estimatedMonthlyPayments.taxesAmount = calculatorMortgageDetailsInput.taxesEscrow;
  refiClosingCosts.estimatedMonthlyPayments.hoaDuesAmount = closingCostTotals.data.prepaidExpenses.hoaDues;
  refiClosingCosts.estimatedMonthlyPayments.totalMonthlyPayment = 0; // REVISIT

  refiClosingCosts.estimatedClosingCosts = new EstimatedClosingCosts();
  refiClosingCosts.estimatedClosingCosts.underwritingFee = closingCostTotals.data.closingCosts.underwritingFee;
  refiClosingCosts.estimatedClosingCosts.processingFee = closingCostTotals.data.closingCosts.processingFee;
  refiClosingCosts.estimatedClosingCosts.discountPoints = closingCostTotals.data.closingCosts.discountPoints;
  refiClosingCosts.estimatedClosingCosts.taxServiceFee = closingCostTotals.data.closingCosts.taxServiceFee;
  refiClosingCosts.estimatedClosingCosts.initialPmi = closingCostTotals.data.closingCosts.initialPMI;
  refiClosingCosts.estimatedClosingCosts.settlement = closingCostTotals.data.closingCosts.settlement;
  refiClosingCosts.estimatedClosingCosts.closingFee = 0; // REVISIT
  refiClosingCosts.estimatedClosingCosts.taxCertificate = closingCostTotals.data.closingCosts.taxCertificate;
  refiClosingCosts.estimatedClosingCosts.closingProtectionLetter = closingCostTotals.data.closingCosts.cpl;
  refiClosingCosts.estimatedClosingCosts.transferTax = closingCostTotals.data.closingCosts.transferTax;
  refiClosingCosts.estimatedClosingCosts.transferFee = closingCostTotals.data.closingCosts.transferFee;
  refiClosingCosts.estimatedClosingCosts.appraisalFee = closingCostTotals.data.closingCosts.appraisalFee;
  refiClosingCosts.estimatedClosingCosts.creditReport = closingCostTotals.data.closingCosts.creditReport;
  refiClosingCosts.estimatedClosingCosts.employmentVerification = closingCostTotals.data.closingCosts.employmentVerification;
  refiClosingCosts.estimatedClosingCosts.floodDeterminationFee = closingCostTotals.data.closingCosts.floodDeterminationFee;
  refiClosingCosts.estimatedClosingCosts.lendersTitleInsurance = closingCostTotals.data.closingCosts.lenderTitleInsurance;
  refiClosingCosts.estimatedClosingCosts.deedOfTrust = closingCostTotals.data.closingCosts.recording;

  refiClosingCosts.estimatedPrepaidExpenses = new PrepaidExpenses();
  refiClosingCosts.estimatedPrepaidExpenses.interimInterestDays = 30;
  refiClosingCosts.estimatedPrepaidExpenses.interimInterestAmount = closingCostTotals.data.prepaidExpenses.interimInterestAmount;
  refiClosingCosts.estimatedPrepaidExpenses.homeownersInsuranceMonths = closingCostTotals.data.prepaidExpenses.homeOwnerInsEscrowMonths;
  refiClosingCosts.estimatedPrepaidExpenses.homeownersInsuranceAmount = closingCostTotals.data.prepaidExpenses.homeOwnerInsEscrow;
  refiClosingCosts.estimatedPrepaidExpenses.homeownersInsurancePrepaidMonths = 0;
  refiClosingCosts.estimatedPrepaidExpenses.homeownersInsurancePrepaidAmount = closingCostTotals.data.prepaidExpenses.homeOwnerInsPrePaid;
  refiClosingCosts.estimatedPrepaidExpenses.hoaDuesMonths = 0;
  refiClosingCosts.estimatedPrepaidExpenses.hoaDues = closingCostTotals.data.prepaidExpenses.hoaDues;
  refiClosingCosts.estimatedPrepaidExpenses.pmiMonths = 0;
  refiClosingCosts.estimatedPrepaidExpenses.pmiAmount = closingCostTotals.data.prepaidExpenses.pmi;
  refiClosingCosts.estimatedPrepaidExpenses.floodInsEscrowMonths = 3;
  refiClosingCosts.estimatedPrepaidExpenses.floodInsEscrowAmount = closingCostTotals.data.prepaidExpenses.floodInsuranceEscrow;
  refiClosingCosts.estimatedPrepaidExpenses.taxEscrowMonths = closingCostTotals.data.prepaidExpenses.monthsOfTaxes;
  refiClosingCosts.estimatedPrepaidExpenses.taxEscrowAmount = closingCostTotals.data.prepaidExpenses.taxInsEscrow;

  refiClosingCosts.fundsAtClosing = new FundsAtClosing();
  refiClosingCosts.fundsAtClosing.closingCosts = calculationResult.closingCostsTotal;
  refiClosingCosts.fundsAtClosing.prepaidExpenses = calculationResult.prepaidExpensesTotal.toFixed(0);
  refiClosingCosts.fundsAtClosing.totalFundsAtClosing = calculationResult.cashToClosingTotal;
  refiClosingCosts.fundsAtClosing.firstLoan = closingCostTotals.data.cashToClosing.payoffFirstLoan;
  refiClosingCosts.fundsAtClosing.secondLoan = closingCostTotals.data.cashToClosing.payoffSecondLoan;
  refiClosingCosts.fundsAtClosing.baseLoan = closingCostTotals.data.cashToClosing.baseLoanAmount.toFixed(0);
  refiClosingCosts.fundsAtClosing.addlLoan = closingCostTotals.data.cashToClosing.additionalLoanAmount;
  refiClosingCosts.fundsAtClosing.calculatedLoanAmount = parseFloat(refiClosingCosts.fundsAtClosing.baseLoan) + parseFloat(refiClosingCosts.fundsAtClosing.addlLoan);
  return refiClosingCosts;
};
